import type {HTMLAttributes} from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import {alpha} from '@mui/material/styles';

import type {ResolvedOption} from './selectOption';

interface SelectOptionRowProps {
    option: ResolvedOption;
    itemProps: HTMLAttributes<HTMLLIElement>;
    /** Omit for SingleSelect; false still renders an unchecked checkbox. */
    checked?: boolean;
}

const SelectOptionRow = ({option, itemProps, checked}: SelectOptionRowProps) => {
    // MUI includes key in these props; React warns if it is spread into JSX.
    // CSS owns pointer hover so headings cannot leave a stale keyboard target behind.
    const {key: _key, onMouseMove: _onMouseMove, ...liProps} = itemProps as typeof itemProps & {key?: string};

    return (
        // Match MUI's option specificity to preserve the minimum row height on desktop.
        <Box
            component={'li'}
            {...liProps}
            sx={theme => ({
                gap: 1,
                '&.MuiAutocomplete-option': {minHeight: 40},
                '@media (hover: hover)': {
                    '&.MuiAutocomplete-option:hover:not(.Mui-focusVisible)': {
                        bgcolor: 'action.hover',
                        '&[aria-selected="true"]': {
                            bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity)
                        }
                    }
                }
            })}
        >
            {checked !== undefined && (
                <Checkbox
                    checked={checked}
                    size={'small'}
                    tabIndex={-1}
                    disableRipple
                    aria-hidden
                    sx={{p: 0, pointerEvents: 'none'}}
                />
            )}

            {option.adornment}

            <Typography variant={'body1'} sx={{flex: 1, minWidth: 0}}>
                {option.label}
            </Typography>

            {!!option.detail && (
                // Capped at half the row so a long email never squeezes the label away.
                <Typography variant={'body2'} color={'text.secondary'} noWrap sx={{minWidth: 0, maxWidth: '50%'}}>
                    {option.detail}
                </Typography>
            )}
        </Box>
    );
};

export default SelectOptionRow;
